import React, { useEffect } from 'react';
import _, { Dictionary } from 'lodash';
import { getTemplates } from '../models/api/templates';
import { DocumentTemplateType } from '../api-client/autogenerated';
import { useSelector } from 'react-redux';
import { getUserState } from '../features/user/selectors';

interface IAppUtilsContext {
  documentTemplates: {
    documentTemplateNameToId: Dictionary<string>;
    documentTemplateIdToName: Dictionary<DocumentTemplateType>;
    getTemplateId: (type: string) => string;
    getTemplateName: (templateId: string) => DocumentTemplateType;
  };
}

export const AppUtilsContext = React.createContext<IAppUtilsContext | null>(null);

export const useAppUtilsContext = (): IAppUtilsContext => {
  const ctx = React.useContext(AppUtilsContext);
  if (!ctx) {
    throw new Error('useMainPortal must be used within a MainPortalProvider');
  }
  return ctx;
};

export const AppUtilsProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const user = useSelector(getUserState);

  const [documentTemplateNameToId, setDocumentTemplateNameToId] = React.useState<
    Dictionary<string>
  >({});

  const documentTemplateIdToName = React.useMemo(
    () => _.invert(documentTemplateNameToId) as Dictionary<DocumentTemplateType>,
    [documentTemplateNameToId],
  );

  useEffect(() => {
    if (user.id) {
      getTemplates().then(setDocumentTemplateNameToId);
    }
  }, [user.id]);

  const getTemplateId = (type: string) => {
    return documentTemplateNameToId[type];
  };

  const getTemplateName = (templateId: string) => {
    return documentTemplateIdToName[templateId];
  };

  return (
    <AppUtilsContext.Provider
      value={{
        documentTemplates: {
          documentTemplateNameToId,
          documentTemplateIdToName,
          getTemplateName,
          getTemplateId,
        },
      }}
    >
      {children}
    </AppUtilsContext.Provider>
  );
};

export default AppUtilsContext;
